import { Platform } from 'react-native';

const METRO_HOST = Platform.OS === 'android' ? '10.0.2.2' : 'localhost';
const METRO_PORT = '8081';

export type SimulatedErrorType =
  | 'none'
  | 'network'
  | 'server'
  | 'clientError'
  | 'rateLimit'
  | 'unauthorized'
  | 'forbidden'
  | 'timeout'
  | 'clientTimeout'
  | 'graphql';

interface NetworkErrorStatusResponse {
  errorMode: SimulatedErrorType | null;
  once?: boolean;
  availableErrors?: string[];
  message?: string;
}

interface SetNetworkErrorOptions {
  /** Auto-disable after the first intercepted request — for retry testing */
  once?: boolean;
}

export const setNetworkError = async (
  errorType: SimulatedErrorType,
  options?: SetNetworkErrorOptions,
): Promise<SimulatedErrorType | null> => {
  try {
    const onceParam = options?.once ? '&once=true' : '';
    const response = await fetch(
      `http://${METRO_HOST}:${METRO_PORT}/set-network-error?type=${errorType}${onceParam}`,
    );
    const data = (await response.json()) as NetworkErrorStatusResponse;
    return data.errorMode;
  } catch {
    // Metro not reachable — simulation unavailable
    return null;
  }
};

export const getNetworkErrorStatus =
  async (): Promise<SimulatedErrorType | null> => {
    try {
      const response = await fetch(
        `http://${METRO_HOST}:${METRO_PORT}/network-error-status`,
      );
      const data = (await response.json()) as NetworkErrorStatusResponse;
      return data.errorMode;
    } catch {
      // Metro not reachable — simulation unavailable
      return null;
    }
  };

export const clearNetworkError = async (): Promise<void> => {
  await setNetworkError('none');
};

export const ERROR_TYPE_LABELS: Record<SimulatedErrorType, string> = {
  none: 'None (Normal)',
  network: 'Network Error (503)',
  server: 'Server Error (500)',
  clientError: 'Client Error (400)',
  rateLimit: 'Rate Limit (429)',
  unauthorized: 'Unauthorized (401)',
  forbidden: 'Forbidden (403)',
  timeout: 'Timeout (408)',
  clientTimeout: 'Client Timeout (hang)',
  graphql: 'GraphQL Error',
};

type ErrorModeListener = () => void;
const listeners = new Set<ErrorModeListener>();

/** Subscribe to error-mode changes. Returns an unsubscribe function. */
export const onErrorModeChange = (fn: ErrorModeListener) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};

/** Notify all subscribers that the error mode changed. */
export const notifyErrorModeChange = () => {
  listeners.forEach((fn) => fn());
};
